import type { Integer } from '@hc-frontend/core-types';
import { ProductType } from '@hc-frontend/core-types';
import {
  createErrorOperationResult,
  createSuccessOperationResult,
} from '@hc-frontend/core-utils-business-operation';
import {
  UpdateProspectDto,
  updateProspectService,
} from '@hc-frontend/services';
import { userAgent as getUserAgent } from 'next/server';

import type { UpdateProspectJourneyRepository } from '../../domain';

function productTypeToLeadProductType(productType: ProductType) {
  const productMap: Record<ProductType, Integer> = {
    [ProductType.Enum.HEALTH]: 4,
    [ProductType.Enum.MEDICARE]: 3,
    [ProductType.Enum.MEDICARE_ADVANTAGE]: 3,
  };

  return productMap[productType];
}

export function updateProspectJourneyAdapterFactory({
  baseURL,
}: {
  baseURL: string;
}): UpdateProspectJourneyRepository {
  return async (parameters) => {
    const { headers } = await import('next/headers');
    try {
      const userAgent = getUserAgent({ headers: await headers() }).ua;
      const isMobile = /android.+mobile|ip(hone|[oa]d)/i.test(userAgent);

      const payload: UpdateProspectDto = {
        sessionId: parameters.sessionId,
        userId: parameters.userId,
        journeyId: parameters.journeyId,
        productTypeId: productTypeToLeadProductType(parameters.productType),
        additionalQuestions: {
          is_phoenix: parameters.additionalQuestions?.IS_PHOENIX,
          askForZipCode: parameters.additionalQuestions?.ASK_FOR_ZIP_CODE,
          mobile: isMobile ? 'y' : 'n',
        },
        domainSessionId: parameters.domainSessionId,
        domainUserId: parameters.domainUserId,
        standardizedMaterialId: parameters.standardizedMaterialId,
      };

      const response = await updateProspectService(baseURL, payload);

      if (response.result !== 'success') {
        return createErrorOperationResult({
          errors: [
            {
              code: 'failedToUpdateProspectJourney',
              rootCause: new Error('Failed to update prospect journey.'),
            },
          ],
        });
      }

      return createSuccessOperationResult(undefined);
    } catch (error) {
      return createErrorOperationResult({
        errors: [
          {
            code: 'failedToUpdateProspectJourney',
            rootCause: error as Error,
          },
        ],
      });
    }
  };
}
